import { useState } from "react";
import { Plus, Pencil, X, Coins } from "lucide-react";
import { trpc } from "../lib/trpc";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { VEHICLE_TYPES, SURCHARGE_PRESETS, type Surcharge } from "../../../shared/const";
import { totalCost } from "../../../shared/fleet-cost";

type FleetRow = { id: number; companyName: string; region: "taiwan" | "hongkong" };
type CostRow = {
  fleetId: number;
  vehicleType: string;
  baseCost: string;
  currency: "TWD" | "HKD" | "USD";
  surcharges: unknown;
};

const regionLabel = (r: string) =>
  r === "taiwan"
    ? { "zh-TW": "台灣", "en-US": "Taiwan", "zh-CN": "台湾" }
    : { "zh-TW": "香港", "en-US": "Hong Kong", "zh-CN": "香港" };

export default function FleetCosts() {
  const { t } = useLanguage();
  const fleetsQ = trpc.party.listFleets.useQuery();
  const costsQ = trpc.fleetCost.list.useQuery();

  const fleets = (fleetsQ.data ?? []) as FleetRow[];
  const costs = (costsQ.data ?? []) as CostRow[];
  const costMap = new Map<string, CostRow>();
  costs.forEach((c) => costMap.set(`${c.fleetId}:${c.vehicleType}`, c));

  const refetch = () => costsQ.refetch();
  const loading = fleetsQ.isLoading || costsQ.isLoading;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-medium tracking-tight">
          {t({ "zh-TW": "車費成本", "en-US": "Fleet Costs", "zh-CN": "车费成本" })}
        </h1>
        <p className="text-[13px] text-muted-foreground">
          {t({
            "zh-TW": "維護各供應商每車型的成本與固定加購項（如安全座椅）。供調度員報價/派遣時參考，不對外顯示、不自動計算售價。",
            "en-US": "Maintain each fleet's cost per vehicle type and fixed add-ons. Shown to dispatchers as reference only.",
            "zh-CN": "维护各供应商每车型的成本与固定加购项。供调度员报价/派遣时参考，不对外显示。",
          })}
        </p>
      </div>

      {loading ? (
        <div className="h-24 animate-pulse rounded-lg bg-muted/40" />
      ) : fleets.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-[13px] text-muted-foreground">
            {t({ "zh-TW": "尚無供應商。請先於「合作方管理」建立車隊。", "en-US": "No fleets yet. Add fleets under Parties first.", "zh-CN": "尚无供应商。请先于「合作方管理」建立车队。" })}
          </CardContent>
        </Card>
      ) : (
        fleets.map((f) => (
          <Card key={f.id}>
            <CardContent className="p-0">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                  <Coins className="h-4 w-4 text-muted-foreground" />
                  <span className="font-display text-[15px] font-medium">{f.companyName}</span>
                  <span className="font-mono text-[12px] uppercase tracking-wider text-muted-foreground">
                    {t(regionLabel(f.region))}
                  </span>
                </div>
              </div>
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-border text-left font-mono text-[12px] uppercase tracking-wider text-muted-foreground">
                    <th className="p-3">{t({ "zh-TW": "車型", "en-US": "Vehicle", "zh-CN": "车型" })}</th>
                    <th className="p-3 text-right">{t({ "zh-TW": "成本合計", "en-US": "Total cost", "zh-CN": "成本合计" })}</th>
                    <th className="p-3">{t({ "zh-TW": "加購項", "en-US": "Add-ons", "zh-CN": "加购项" })}</th>
                    <th className="p-3 text-right">{t({ "zh-TW": "操作", "en-US": "Action", "zh-CN": "操作" })}</th>
                  </tr>
                </thead>
                <tbody>
                  {VEHICLE_TYPES.map((v) => {
                    const c = costMap.get(`${f.id}:${v.value}`);
                    const surcharges = ((c?.surcharges as Surcharge[] | null) ?? []) as Surcharge[];
                    return (
                      <tr key={v.value} className="border-b border-border/60 last:border-0">
                        <td className="p-3">{t(v.label)}</td>
                        <td className="p-3 text-right numeric font-medium">
                          {c
                            ? `${c.currency} ${totalCost(Number(c.baseCost), surcharges).toLocaleString()}`
                            : <span className="text-muted-foreground/60">{t({ "zh-TW": "未設定", "en-US": "unset", "zh-CN": "未设定" })}</span>}
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {surcharges.length
                            ? surcharges.map((s) => s.label).join("、")
                            : "—"}
                        </td>
                        <td className="p-3 text-right">
                          <CostDialog fleet={f} vehicleType={v.value} existing={c} onDone={refetch} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

function CostDialog({
  fleet,
  vehicleType,
  existing,
  onDone,
}: {
  fleet: FleetRow;
  vehicleType: string;
  existing?: CostRow;
  onDone: () => void;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [baseCost, setBaseCost] = useState(existing ? Number(existing.baseCost) : 0);
  const [surcharges, setSurcharges] = useState<Surcharge[]>(
    () => ((existing?.surcharges as Surcharge[] | null) ?? []) as Surcharge[],
  );
  const currency = "HKD" as const; // D10：車費成本一律 HKD（港幣中心、不換算）
  const vehicleLabel = VEHICLE_TYPES.find((v) => v.value === vehicleType)?.label ?? { "zh-TW": vehicleType, "en-US": vehicleType, "zh-CN": vehicleType };

  const m = trpc.fleetCost.upsert.useMutation({
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });

  const setS = (i: number, patch: Partial<Surcharge>) =>
    setSurcharges((arr) => arr.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const addS = (label = "") => setSurcharges((arr) => [...arr, { label, amount: 0 }]);
  const rmS = (i: number) => setSurcharges((arr) => arr.filter((_, idx) => idx !== i));

  const save = () =>
    m.mutate({
      fleetId: fleet.id,
      vehicleType,
      baseCost,
      surcharges: surcharges.filter((s) => s.label.trim() !== ""),
    });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {existing ? (
          <Button variant="ghost" size="sm">
            <Pencil className="h-3.5 w-3.5" />
            {t({ "zh-TW": "編輯", "en-US": "Edit", "zh-CN": "编辑" })}
          </Button>
        ) : (
          <Button size="sm" variant="outline">
            <Plus className="h-3.5 w-3.5" />
            {t({ "zh-TW": "設定", "en-US": "Set", "zh-CN": "设定" })}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(vehicleLabel)} · {fleet.companyName}
          </DialogTitle>
          <DialogDescription>
            {t({ "zh-TW": "一律以港幣（HKD）計價。加購項為固定金額，估算時加總於基礎成本。", "en-US": "Priced in HKD. Add-ons are fixed amounts added to base cost.", "zh-CN": "一律以港币（HKD）计价。加购项为固定金额，估算时加总于基础成本。" })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>
              {t({ "zh-TW": "基礎成本", "en-US": "Base cost", "zh-CN": "基础成本" })}（{currency}）
            </Label>
            <Input type="number" min={1} value={baseCost} onChange={(e) => setBaseCost(Number(e.target.value))} />
          </div>

          <div className="space-y-2">
            <Label>{t({ "zh-TW": "固定加購項", "en-US": "Fixed add-ons", "zh-CN": "固定加购项" })}</Label>
            <div className="flex flex-wrap gap-1.5">
              {SURCHARGE_PRESETS.map((p) => (
                <button
                  key={p["en-US"]}
                  type="button"
                  onClick={() => addS(t(p))}
                  className="rounded-full border border-border px-2.5 py-1 text-[12px] text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                >
                  + {t(p)}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              {surcharges.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    className="flex-1"
                    placeholder={t({ "zh-TW": "項目名稱", "en-US": "Label", "zh-CN": "项目名称" })}
                    value={s.label}
                    onChange={(e) => setS(i, { label: e.target.value })}
                  />
                  <Input
                    type="number"
                    min={0}
                    className="w-28"
                    placeholder={currency}
                    value={s.amount}
                    onChange={(e) => setS(i, { amount: Number(e.target.value) })}
                  />
                  <button type="button" onClick={() => rmS(i)} className="text-muted-foreground hover:text-destructive">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <Button type="button" variant="ghost" size="sm" onClick={() => addS()}>
                <Plus className="h-3.5 w-3.5" />
                {t({ "zh-TW": "新增加購項", "en-US": "Add add-on", "zh-CN": "新增加购项" })}
              </Button>
            </div>
          </div>

          <div className="flex items-baseline justify-between border-t border-border pt-3 text-[13px]">
            <span className="text-muted-foreground">{t({ "zh-TW": "成本合計", "en-US": "Total cost", "zh-CN": "成本合计" })}</span>
            <span className="numeric text-base font-medium">
              {currency} {totalCost(baseCost, surcharges.filter((s) => s.label.trim() !== "")).toLocaleString()}
            </span>
          </div>

          {m.error && <p className="text-[12px] text-destructive">{m.error.message}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}
          </Button>
          <Button onClick={save} disabled={m.isPending || baseCost <= 0}>
            {t({ "zh-TW": "儲存", "en-US": "Save", "zh-CN": "保存" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
